// TODO: lift subscriptions to Dashboard level
import { useEffect, useMemo, useState } from 'react'
import { useYear } from '../auth/YearContext'
import {
  subscribeYear,
  subscribeReceipts,
  aggregateReceipts,
  aggregateReceiptsByCategory,
  type CloudYearDoc,
  type CloudReceipt,
} from '../lib/cloudStore'
import { profitLossYTD } from '../data/store'
import type { CompanyYearData } from '../data/store'
import { WinButtons, currentSize, type CardSizeProps } from './WinButtons'
import { smoothPath } from '../lib/chart'
import { emptyMonths } from '../data/store'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props extends CardSizeProps { companyId: string; companyName: string }

export function ProfitLossCard(props: Props) {
  const { companyId } = props
  const size = currentSize(props)
  const { year } = useYear()
  const [data, setData] = useState<CloudYearDoc>({ year, months: emptyMonths() })
  const [receipts, setReceipts] = useState<CloudReceipt[]>([])

  useEffect(() => {
    try {
      return subscribeYear(companyId, year, setData)
    } catch {
      /* not authed */
      return () => {}
    }
  }, [companyId, year])

  useEffect(() => {
    try {
      return subscribeReceipts(companyId, year, setReceipts)
    } catch {
      /* not authed */
      return () => {}
    }
  }, [companyId, year])

  const receiptMonths = useMemo(() => aggregateReceipts(receipts), [receipts])
  const catMonths = useMemo(() => aggregateReceiptsByCategory(receipts), [receipts])
  const sieMonths = data.months.length === 12 ? data.months : emptyMonths()

  const months = useMemo(() => {
    return sieMonths.map((sie, i) => {
      const rec = receiptMonths[i]
      const sieHasData = sie.income !== 0 || sie.expense !== 0
      return {
        month: i,
        vatIn: sie.vatIn,
        vatOut: sie.vatOut,
        income: sieHasData ? sie.income : rec.income,
        expense: sieHasData ? sie.expense : rec.expense,
      }
    })
  }, [sieMonths, receiptMonths])

  const hasReceiptData = receiptMonths.some((m) => m.income !== 0 || m.expense !== 0)
  const hasSieData = sieMonths.some((m) => m.income !== 0 || m.expense !== 0)
  // Källa: P&L kommer från kundfakturor (intäkter) + leverantörsfakturor & utlägg (kostnader)
  const dataSource = hasSieData && hasReceiptData ? 'SIE + Fakturor + Kvitton' : hasSieData ? 'SIE' : hasReceiptData ? 'Fakturor + Kvitton' : null

  const { income, expense, pl } = profitLossYTD({ year, months } as CompanyYearData)
  const color = pl >= 0 ? 'value--pos' : 'value--neg'
  const label = pl >= 0 ? 'Vinst hittills i \u00e5r' : 'F\u00f6rlust hittills i \u00e5r'

  const now = new Date()
  const uptoMonth = year === now.getFullYear() ? now.getMonth() : 11

  const maxAbs = Math.max(1, ...months.map((m) => Math.max(m.income, m.expense)))
  const visible = months.slice(0, uptoMonth + 1)
  const hasAnyData = visible.some((m) => m.income !== 0 || m.expense !== 0)

  const W = 360
  const H = 80
  const pad = 6
  function toPts(getValue: (m: typeof visible[0]) => number): [number, number][] {
    return visible.map((m, i) => {
      const x = visible.length === 1 ? W / 2 : pad + (i / (visible.length - 1)) * (W - pad * 2)
      const y = H - pad - (getValue(m) / maxAbs) * (H - pad * 2)
      return [x, y] as [number, number]
    })
  }

  if (size === "min") {
    return (
      <div className="card card--collapsed" style={{ borderLeftColor: pl >= 0 ? '#10b981' : '#ef4444' }}>
        <div className="card__header-row">
          <span className="card__label" style={{ margin: 0 }}>Vinst/F&#246;rlust</span>
          <span className={'card__mini-value ' + color}>{fmt(pl)} kr</span>
          <WinButtons current="min" {...props} />
        </div>
      </div>
    )
  }

  return (
    <div className={'card card--accent' + (size === 'med' ? ' card--medium' : '')} style={{ borderLeftColor: pl >= 0 ? '#10b981' : '#ef4444' }}>
      <div className="card__header-row">
        <div className="card__label" style={{ margin: 0 }}>{'Vinst / f\u00f6rlust nu (' + year + ')'}</div>
        <WinButtons current={size} {...props} />
      </div>
      <div className={'card__value ' + color}>{fmt(pl)} kr</div>
      <div className="card__sub">{label}</div>

      <div style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        {'Int\u00e4kter: '}<b style={{ color: 'var(--text)' }}>{fmt(income)}</b>{' kr'}
        <br />
        {'Kostnader: '}<b style={{ color: 'var(--text)' }}>{fmt(expense)}</b>{' kr'}
        {dataSource && (
          <>
            <br />
            <span style={{ fontSize: '0.75rem' }}>{'K\u00e4lla: ' + dataSource}</span>
          </>
        )}
      </div>

      <div className="card__divider" />

      <div className="card__label">{'Int\u00e4kter vs kostnader'}</div>
      {hasAnyData ? (
        <>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'right', marginBottom: 2 }}>
            {'Max: ' + fmt(maxAbs) + ' kr'}
          </div>
          <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 80, marginTop: 2 }} preserveAspectRatio="none">
            <path d={smoothPath(toPts((m) => m.income))} fill="none" stroke="#10b981" strokeWidth="2" />
            <path d={smoothPath(toPts((m) => m.expense))} fill="none" stroke="#ef4444" strokeWidth="2" opacity="0.8" />
          </svg>
        </>
      ) : (
        <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', opacity: 0.5 }}>
          {'Ingen data'}
        </div>
      )}
      <div className="sparkline__label-row">
        {MONTHS.slice(0, uptoMonth + 1).map((m) => (
          <span key={m} className="sparkline__label">{m}</span>
        ))}
      </div>

      <div className="card__label" style={{ marginTop: '0.5rem' }}>{'Per m\u00e5nad'}</div>
      <div className="months">
        {/* Header \u2014 m\u00e5nadsnamn + Netto + Kund/Lev/Utl\u00e4gg (belopp exkl moms) */}
        <div className="months__row months__row--head">
          <span className="months__cell months__cell--name">{''}</span>
          <span className="months__cell">{'Netto'}</span>
          <span className="months__cell">{'Kund'}</span>
          <span className="months__cell">{'Lev'}</span>
          <span className="months__cell">{'Utl\u00e4gg'}</span>
        </div>
        {visible.map((m, i) => {
          const net = m.income - m.expense
          const hasData = m.income !== 0 || m.expense !== 0
          const cat = catMonths[i]
          return (
            <div className="months__row" key={i} style={{ opacity: hasData ? 1 : 0.4 }}>
              <span className="months__cell months__cell--name">{MONTHS[i]}</span>
              <span
                className="months__cell months__cell--net"
                style={{ color: hasData ? (net >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)' }}
              >
                {hasData ? fmt(net) : '\u2014'}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.kund.net ? fmt(cat.kund.net) : '\u2014'}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.lev.net ? '\u2212' + fmt(cat.lev.net) : '\u2014'}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.utl.net ? '\u2212' + fmt(cat.utl.net) : '\u2014'}
              </span>
            </div>
          )
        })}
      </div>
      {months.every((m) => m.income === 0 && m.expense === 0) && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          {'Inga int\u00e4kter/kostnader inlagda \u00e4n. Importera SIE eller kvitton via \ud83d\udce5 Import.'}
        </div>
      )}
    </div>
  )
}
